/* eslint-disable array-bracket-newline */
/**
 * This hook delivers information about:
 * a) Pool (actually Ticker)
 * b) Coin
 * Than it merges it into one payload
 */
import { useState, useEffect } from 'react';
import { ITokenInfo, ITokenScore } from '@kattana-io/common';
import { KattanaAPI } from '../../../store/createStore';
import { ITickerDisplayData } from '../../../hooks/useTicker';
import { ICurrentState } from '../../../reducers/current';

export type PoolData = Record<string, number>;

const REFRESH_TIME = 30000;

const useDexPoolInfo = (
  ticker: ITickerDisplayData,
  currentState: ICurrentState
) => {
  const { chain, base_address, base, quote, base_reserve, quote_reserve } =
    ticker;

  const [loading, setLoading] = useState(true);
  const [tokenInfo, setTokenInfo] = useState<ITokenInfo | null>(null);
  const [tokenScore, setTokenScore] = useState<ITokenScore | null>(null);

  const poolData: PoolData = {
    'Pool created': currentState.pool_created,
    'Pool liquidity': currentState.liquidity,
    [`Pooled ${base}`]: base_reserve,
    [`Pooled ${quote}`]: quote_reserve,
    'Daily volume': currentState.volume,
  };

  if (currentState.annual_percentage_yield) {
    poolData['APY'] = currentState.annual_percentage_yield;
  }

  const fetchTokenInfo = async (
    chain: string,
    address: string,
    controller: AbortController,
    refresh: boolean
  ) => {
    if (chain && address) {
      try {
        if (!refresh) {
          setLoading(true);
        }
        const params = {
          news: true,
        };
        const info = await KattanaAPI.tokenInfo(
          chain,
          address,
          params,
          { signal: controller.signal }
        );

        if(!controller.signal.aborted) {
          setTokenInfo(info);
        }
      } catch (error) {
        console.log(error);
        if (!controller.signal.aborted) {
          setTokenScore(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    }
  };

  const fetchTokenScore = async (
    chain: string,
    address: string,
    controller: AbortController
  ) => {
    if (chain && address) {
      try {
        const score = await KattanaAPI.tokenScore(chain, address, {
          signal: controller.signal,
        });

        if(!controller.signal.aborted) {
          setTokenScore(score);
        }
      } catch (error) {
        if (!controller.signal.aborted) {
          setTokenScore(null);
        }
      }
    }
  };

  /**
   * Fetch aggregated data
   */
  useEffect(() => {
    const controller = new AbortController();

    fetchTokenInfo(chain, base_address, controller, false);
    fetchTokenScore(chain, base_address, controller);

    const interval = setInterval(() => {
      fetchTokenInfo(chain, base_address, controller, true);
    }, REFRESH_TIME);

    return () => {
      clearInterval(interval);
      controller.abort();
    };
  }, [base_address, chain]);

  return {
    loading,
    tokenInfo,
    tokenScore,
    poolData,
  };
};

export default useDexPoolInfo;
